import { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Hidden } from '@material-ui/core';

import {
  useAddProductToBookmarksMutation,
  useGetShoppingCartDetailsQuery,
  useRemoveProductFromCartMutation,
} from 'core/services';

import useIsLoading from 'hooks/useIsLoading.hook';
import useMessage from 'hooks/useMessage.hook';
import useMobile from 'hooks/useMobile.hook';

import { getProductLink } from 'utils/helpers';

import AddToCardButton from 'components/AddToCardButton';
import AttributesTooltype from 'components/AttributesTooltype';
import Icon from 'components/Icon';
import IconButton from 'components/IconButton';
import Price from 'components/Price';
import Space from 'components/Space';
import useSkipForProduct from 'hooks/useSkipForProduct.hook';
import ProductStatus from 'components/ProductStatus';

const CardItem = ({ style, item }) => {
  const m = useMessage();
  const isMobile = useMobile();
  const cart_id = useSelector((state) => state.cart.cart_id);

  const skipForProduct = useSkipForProduct();
  const cartDetailsQuery = useGetShoppingCartDetailsQuery(
    { cart_id },
    {
      skip: skipForProduct || !cart_id,
    },
  );
  const [removeFromCart, removeFromCartQuery] =
    useRemoveProductFromCartMutation();
  const [addBookmark, addBookmarkQuery] = useAddProductToBookmarksMutation();

  const cartDetails = cartDetailsQuery.currentData;
  const user_permissions = cartDetails?.user_permissions;

  const removeLoading = useIsLoading(removeFromCartQuery);
  const bookmarkLoading = useIsLoading(addBookmarkQuery);

  const handleRemoveFromCart = useCallback(async () => {
    try {
      await removeFromCart({
        cart_id,
        item_id: item.id,
      }).unwrap();
    } catch (ex) {
      m.responseError(ex);
    }
  }, [m, removeFromCart, item, cart_id]);

  const handleAddToBookmarks = useCallback(async () => {
    try {
      handleRemoveFromCart();

      await addBookmark({
        body: {
          product_id: item.product.id,
        },
      }).unwrap();
    } catch (ex) {
      m.responseError(ex);
    }
  }, [m, handleRemoveFromCart, addBookmark, item]);

  const productPrice = item?.price || item.product.price;
  const productUnitPrice = item?.unit_price || item.product.unit_price;

  const productLink = useMemo(() => {
    return getProductLink(item?.product);
  }, [item]);
  const isManipulationsDisabled =
    item.product?.discontinued || item.product?.deleted;
  return (
    <div className={style.cardItem}>
      <Box className={style.titleContainer}>
        <Box flexGrow={1}>
          <div className={style.number}>
            <Link to={productLink}>{item.product.sku}</Link>
          </div>

          <Hidden xsDown>
            <div className={style.title}>
              <Link to={productLink}>{item.product.title}</Link>
            </div>
          </Hidden>
        </Box>

        <Space>
          {user_permissions.can_remove && (
            <IconButton
              size="small"
              variant="contained"
              loading={bookmarkLoading}
              onClick={handleAddToBookmarks}
            >
              <Icon.Favorite fontSize={16} />
            </IconButton>
          )}

          {user_permissions.can_remove && (
            <IconButton
              size="small"
              variant="contained"
              loading={removeLoading}
              onClick={handleRemoveFromCart}
            >
              <Icon.Trash fontSize={16} />
            </IconButton>
          )}
        </Space>
      </Box>

      <Hidden smUp>
        <div className={style.title}>
          <Link to={productLink}>{item.product.title}</Link>
        </div>
      </Hidden>

      <div className={style.company}>{item.product.supplier}</div>
      {item.product?.discontinued && (
        <ProductStatus status="discontinued" mt={1} />
      )}
      {item.product?.deleted && <ProductStatus status="deleted" mt={1} />}
      <div className={style.options}>
        <div className={style.configuration}>
          <span>configuration</span>
          <b>
            <AttributesTooltype attributes={item.product?.attributes} />
          </b>
        </div>
        <div className={style.price}>
          <Box mb={0.5}>
            <Price
              amount={productPrice}
              size={isMobile ? 'medium' : 'small'}
              perUnit={productUnitPrice}
              currency={isMobile ? 'USD' : undefined}
            />
          </Box>

          <AddToCardButton
            size="small"
            product_id={item.product.id}
            width={isMobile ? undefined : 140}
            disabled={isManipulationsDisabled}
          />
        </div>
      </div>
    </div>
  );
};

export default CardItem;
